/**
 * Комментарии. Плоский список с сервера собирается в дерево здесь:
 * сервер отдаёт parentCommentId, а форма дерева — дело представления.
 *
 * §14.1 ограничивает глубину тремя уровнями; за это отвечает CommentThread.
 */

import { useAppSelector } from "@/app/hooks";

import { docKey, fromPlain, newRequestId, type RichDoc, type WsError } from "@taskflow/client-core";
import { useLocation } from "react-router-dom";

import { TaskSection } from "@taskflow/contracts";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  type AttachOptions,
  CommentComposer,
  type CommentNodeData,
  CommentThread,
  ConfirmDialog,
  ErrorState,
  SkeletonRows,
  toast,
} from "@/shared/ui";

import {
  type CommentItem,
  useCommentHighlights,
  useCreateCommentMutation,
  useDeleteAttachmentMutation,
  useDeleteCommentMutation,
  useUpdateCommentMutation,
} from "@taskflow/client-core/tasks";
import { ATTACH_OPTIONS } from "../attachmentRules";
import { useAttachmentFiles } from "../useAttachmentFiles";
import { CommentAttachments } from "./CommentAttachments";
import { Section } from "./Section";

/** Плоский список → дерево. Сироты (родитель удалён) поднимаются в корень,
 *  иначе ответ на удалённый комментарий молча исчезнет из интерфейса. */
function buildTree(
  items: CommentItem[],
  onDownload: (id: string) => void,
  highlighted: ReadonlySet<string>,
): CommentNodeData[] {
  const nodes = new Map<string, CommentNodeData>();
  for (const item of items) {
    nodes.set(item.id, {
      id: item.id,
      highlighted: highlighted.has(item.id),
      author: {
        id: item.authorId,
        firstName: item.authorFirstName,
        lastName: item.authorLastName,
        avatarUrl: item.authorAvatarUrl,
      },
      text: item.text,
      body: item.body ?? null,
      createdAtLabel: item.createdAtLabel,
      editedLabel: item.editedLabel,
      canEdit: item.canEdit,
      canDelete: item.canDelete,
      attachments: (edit) => (
        <CommentAttachments files={item.attachments} onDownload={onDownload} edit={edit} />
      ),
      attachmentCount: item.attachments.length,
      replies: [],
    });
  }

  const roots: CommentNodeData[] = [];
  for (const item of items) {
    const node = nodes.get(item.id);
    if (!node) continue;
    const parent = item.parentCommentId === null ? null : nodes.get(item.parentCommentId);
    if (parent) parent.replies.push(node);
    else roots.push(node);
  }
  // Сервер отдаёт по возрастанию времени. Свежие обсуждения — первыми;
  // ответы внутри ветки остаются по порядку, иначе разговор читается задом наперёд.
  return roots.reverse();
}

export function TaskComments({
  taskKey,
  canComment,
  canAttach,
}: {
  taskKey: string;
  canComment: boolean;
  /** Право прикладывать файлы (file.add). */
  canAttach: boolean;
}) {
  const userId = useAppSelector((state) => state.auth.user?.id);
  const { comments, highlighted } = useCommentHighlights(taskKey, userId);
  const location = useLocation();
  const targetId = location.hash.startsWith("#comment-")
    ? decodeURIComponent(location.hash.slice(9))
    : new URLSearchParams(location.search).get("commentId");
  const containerRef = useRef<HTMLDivElement>(null);
  const positioned = useRef<string | null>(null);
  useEffect(() => {
    if (!targetId || positioned.current === targetId) return;
    const target = containerRef.current?.querySelectorAll<HTMLElement>("[id]");
    const comment = Array.from(target ?? []).find((element) => element.id === `comment-${targetId}`);
    if (!comment) return;
    const frame = requestAnimationFrame(() => {
      comment.scrollIntoView({ block: "center", behavior: "smooth" });
      positioned.current = targetId;
    });
    return () => cancelAnimationFrame(frame);
  }, [targetId, comments.data]);
  const [create] = useCreateCommentMutation();
  const { upload, download } = useAttachmentFiles(taskKey);
  const [update] = useUpdateCommentMutation();
  const [remove, { isLoading: removing }] = useDeleteCommentMutation();
  const [removeFile] = useDeleteAttachmentMutation();

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const tree = useMemo(
    () => buildTree(comments.data?.data ?? [], (id) => void download(id), highlighted),
    [comments.data, download, highlighted],
  );

  const attach: AttachOptions | undefined = canAttach ? ATTACH_OPTIONS : undefined;

  /** true — комментарий создан. Файлы грузятся к нему после: без id их некуда приложить.
   *  Текст может быть пустым, если есть файлы, — картинку отправляют без подписи. */
  const submit = async (
    parentId: string | null,
    text: string,
    files: File[],
    body: RichDoc | null,
  ) => {
    let commentId: string;
    try {
      const comment = await create({
        key: taskKey,
        text,
        body,
        parentCommentId: parentId,
        // §22.3: повторная отправка с тем же ключом не создаёт дубль.
        clientRequestId: newRequestId(),
        ...(files.length > 0 ? { withFiles: true } : {}),
      }).unwrap();
      commentId = comment.id;
    } catch (raw) {
      toast.error(raw as WsError);
      return false;
    }
    // Комментарий уже есть: неудачный файл не отменяет его, об ошибке
    // сообщает загрузка — файл можно приложить повторно ответом.
    let uploaded = 0;
    for (const file of files) if (await upload(file, commentId)) uploaded += 1;
    // Без текста комментарий держался только на файлах. Не загрузился ни
    // один — пустой комментарий убирается, а файлы остаются в поле ввода.
    if (text === "" && uploaded === 0) {
      await remove({ id: commentId })
        .unwrap()
        .catch(() => undefined);
      return false;
    }
    return true;
  };

  /** Версия берётся из загруженного списка, а не из константы: после
   *  первой же правки она растёт, и «1» давала 409 на второй (§22.2). */
  const edit = async (
    id: string,
    text: string,
    files: File[],
    removedFileIds: string[],
    body: RichDoc | null,
  ) => {
    const current = comments.data?.data.find((item) => item.id === id);
    if (current === undefined) return;
    // Новые файлы — первыми: текст можно стереть, только пока у
    // комментария есть файлы, и сервер проверяет это при правке текста.
    for (const file of files) await upload(file, id);
    // Текст могли не трогать — приложили или убрали только файл. Лишний
    // comments.update поднял бы версию и пометил комментарий изменённым без причины.
    const before = current.body ?? (current.text === "" ? null : fromPlain(current.text));
    if (docKey(body) !== docKey(before)) {
      try {
        await update({ id, text, body, version: current.version }).unwrap();
      } catch (raw) {
        toast.error(raw as WsError);
        return;
      }
    }
    for (const fileId of removedFileIds) {
      try {
        await removeFile({ id: fileId }).unwrap();
      } catch (raw) {
        const name = current.attachments.find((file) => file.id === fileId)?.filename;
        toast.error(raw as WsError, name === undefined ? "Не удалось убрать файл" : `Не удалось убрать ${name}`);
      }
    }
  };

  const confirmDelete = async () => {
    if (deleteId === null) return;
    try {
      await remove({ id: deleteId }).unwrap();
      setDeleteId(null);
    } catch (raw) {
      toast.error(raw as WsError);
    }
  };

  return (
    <Section
      title="Комментарии"
      section={TaskSection.COMMENTS}
      forceOpen={targetId !== null || highlighted.size > 0}
      count={comments.data?.data.length}
    >
      {canComment && (
        <div className="mb-4">
          <CommentComposer
            ariaLabel="Новый комментарий"
            placeholder="Написать комментарий"
            {...(attach === undefined ? {} : { attach })}
            onSubmit={(text, files, body) => submit(null, text, files, body)}
          />
        </div>
      )}

      {comments.isLoading && <SkeletonRows rows={3} />}

      {comments.error !== undefined && (
        <ErrorState
          message={(comments.error as WsError).data.message}
          onRetry={() => void comments.refetch()}
        />
      )}

      <div ref={containerRef}>
        {comments.data !== undefined && (
          <CommentThread
            comments={tree}
            canComment={canComment}
            {...(attach === undefined ? {} : { attach })}
            onReply={(parentId, text, files, body) => submit(parentId, text, files, body)}
            onEdit={(id, text, files, removedFileIds, body) =>
              edit(id, text, files, removedFileIds, body)
            }
            onDeleteRequest={setDeleteId}
          />
        )}

      </div>

      <ConfirmDialog
        open={deleteId !== null}
        title="Удалить комментарий"
        description="Комментарий исчезнет из обсуждения вместе с файлами. Ответы на него останутся."
        confirmLabel="Удалить"
        tone="danger"
        loading={removing}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteId(null)}
      />
    </Section>
  );
}
